import { useState } from "react";
import { Plus, Trash2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import { VyberDialog } from "@/components/vyber/VyberDialog";
import {
  MaterialEditDialog,
  type Predvyplnenie,
} from "@/components/cennik/MaterialEditDialog";
import { InaHrubkaDialog } from "@/components/import/InaHrubkaDialog";
import { materialPrimaryId } from "@/lib/format";
import {
  hrubkaRiadku,
  hrubkaViaze,
  inaTrieda,
  type MaterialHrubky,
} from "@/lib/import/hrubkaMaterialu";
import type { DruhMaterialu, Sekcia } from "@/api/schemas";
import type { MapovaniePolozka } from "@/api/hooks/useTopsolidRaw";

/**
 * Výber položky cenníka pre jeden token z exportu.
 *
 * Jeden dialóg pre **materiál, pracovnú dosku, kovanie aj hranu** —
 * pôvodne to bol `HranaDialog` v `TabulkaHran.tsx` a rovnaká obrazovka
 * rozpísaná do riadku pri materiáloch. Dve kópie toho istého výberu by
 * sa časom rozišli.
 *
 * Kurzor ide rovno do **hľadania**: na reálnych dátach návrhy často
 * nesedia (`F001249`, `K682`) a hľadanie je to, čo user naozaj robí.
 *
 * **KOLO 120 — jedna tabuľka namiesto chipov.** User: *„hore máš ako
 * keby zoznam najväčších zhôd, ale zdá sa mi to nadizajnové zle… dávaš
 * mi na výber len nejaké, nie všetky… neviem prečo mi na materiál
 * hrúbky 18 mm zobrazuješ materiály aj iných hrúbok."* Boli to tri
 * veci naraz: chipy ukazovali `slice(0, 6)` z desiatich návrhov,
 * zoznam cenníka pod nimi nemal stránkovanie vôbec, a hrúbka bola
 * v skórovaní len bonus, takže z tých šiestich chipov bolo päť
 * nesprávnej hrúbky.
 *
 * Teraz je to **jeden zoznam**: hore kandidáti so stĺpcom zhody, pod
 * oddeľovačom zvyšok cenníka, na konci „načítať ďalšie". Rozmerový
 * filter je kapsula s ✕ — pozri `rozmerovyFilter`.
 *
 * **KOLO 201 — to isté okno ako každý výber z cenníka** (`VyberDialog`):
 * bodky zhody ostali len tu, akcie importu sú v riadku nad pätičkou.
 */

/** Rozhodnutie usera o jednej položke. */
export type StavPolozky = {
  material_id: string | null;
  zmazat: boolean;
  /** Zapamätať si to na ďalšie importy? */
  zapamatat: boolean;
};

export const PRAZDNY_STAV: StavPolozky = {
  material_id: null,
  zmazat: false,
  // Vypnuté (KOLO 90, rozhodnutie usera): appka si priradenie zapamätá
  // len keď to sám povie. Naučené z minulosti platia ďalej — mení sa,
  // či sa **nové** uloží, nie či sa existujúce použije.
  zapamatat: false,
};

/** Filter cenníka pre daný druh položky. */
export type FilterCennika = {
  druh: DruhMaterialu;
  /** Sekcia cenníka (KOLO 166) — doska, hrana, kovanie alebo sklo. */
  sekcia?: Sekcia;
  zMm?: string;
  /** Šírka hrany (`22`) — plynie z hrúbky dielca. */
  sirkaMm?: string;
  kategoriaIncludeCsv?: string;
};

/**
 * Rozmerový filter pre `MaterialSelect` — tam je kapsula s ✕ (KOLO 201;
 * dovtedy zaškrtávacie políčko „len 18 mm" nad zoznamom).
 *
 * Pri kovaní rozmer nič neznamená, takže kapsula nie je. Pri hrane je to
 * **šírka a blízke**, nie hrúbka: appka si sama nahrádza hrúbku pásky,
 * keď dekor žiadanú nemá (`auto_nahradena`, `topsolid_hrany.py`), takže
 * filter na hrúbku by skryl práve tú hranu, ktorú appka vybrala. A nie
 * presná šírka — 42 aj 43 olepí 36 mm dosku; o koľko presne, vie backend.
 * Hrúbka dosky je exaktná — 18 mm doska sa z 19 mm neurobí.
 */
function rozmerovyFilter(filter: FilterCennika): {
  zMm?: string;
  sirkaMm?: string;
  sirkaPasmo?: boolean;
} {
  if (filter.zMm) return { zMm: filter.zMm };
  if (filter.sirkaMm) return { sirkaMm: filter.sirkaMm, sirkaPasmo: true };
  return {};
}

export function VyberPolozkyDialog({
  polozka,
  stav,
  filter,
  autoPopis,
  kontext,
  predvyplnene,
  menaZostav,
  onZmena,
  onKovanie,
  onClose,
}: {
  polozka: MapovaniePolozka | null;
  stav: StavPolozky;
  filter: FilterCennika;
  /** „Appka vybrala X podľa dekoru" — len pri hranách. */
  autoPopis?: string;
  /** Čo sa priraďuje — dielce v zostavách, kusy kovania, metre hrany
   *  (KOLO 216, `lib/import/vyskyty.ts`). */
  kontext?: string | null;
  /** Predvyplnenie formulára, keď sa položka zakladá do cenníka. */
  predvyplnene?: Predvyplnenie;
  /** Mená zostáv z Tandem Zostáv — do tabuľky dielcov inej hrúbky. */
  menaZostav?: Record<string, string> | null;
  onZmena: (s: StavPolozky, nazov?: string) => void;
  /** „Toto nie je doska, je to kovanie" — len pri plošnom materiáli. */
  onKovanie?: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <>
      {polozka ? (
        // `key` resetuje rozpísaný výber, hľadanie aj rozmerové kapsule
        // pri prepnutí na inú položku — rozhodnutia z predošlej sa
        // neprenášajú
        <TeloVyberu
          key={`${polozka.druh}|${polozka.token}|${polozka.kontext}`}
          polozka={polozka}
          stav={stav}
          filter={filter}
          autoPopis={autoPopis}
          kontext={kontext}
          predvyplnene={predvyplnene}
          menaZostav={menaZostav}
          onZmena={onZmena}
          onKovanie={onKovanie}
          onClose={onClose}
        />
      ) : null}
    </>
  );
}

/**
 * Výber pre jednu položku. Rozhodnutie (položka, Nenahrávať, zapamätať)
 * sa píše do importu až po **Hotovo** alebo dvojkliku — Zrušiť a Esc ho
 * zahodia (KOLO 201: klik označí, potvrdí sa, ako v každom výbere).
 */
function TeloVyberu({
  polozka,
  stav,
  filter,
  autoPopis,
  kontext,
  predvyplnene,
  menaZostav,
  onZmena,
  onKovanie,
  onClose,
}: {
  polozka: MapovaniePolozka;
  stav: StavPolozky;
  filter: FilterCennika;
  autoPopis?: string;
  kontext?: string | null;
  predvyplnene?: Predvyplnenie;
  menaZostav?: Record<string, string> | null;
  onZmena: (s: StavPolozky, nazov?: string) => void;
  onKovanie?: () => void;
  onClose: () => void;
}): JSX.Element {
  const [zakladam, setZakladam] = useState(false);
  const [rozpisane, setRozpisane] = useState<StavPolozky>(stav);
  const [nazov, setNazov] = useState<string | undefined>(undefined);
  /** Materiál vybraný v tomto okne — jeho hrúbka rozhodne o otázke. */
  const [vybrany, setVybrany] = useState<(MaterialHrubky & { id: string }) | null>(null);
  /** Čaká na „Dielce dostanú X mm" — materiál inej hrúbky (KOLO 217). */
  const [inaHrubka, setInaHrubka] = useState<{
    s: StavPolozky;
    n: string | undefined;
    zRiadku: number;
    zMaterialu: number;
  } | null>(null);
  const zhody = polozka.navrhy.map((n) => ({
    material_id: n.material_id,
    skore: n.skore,
    dovod: n.dovod,
  }));
  const dovod = zhody.find((z) => z.material_id === rozpisane.material_id)?.dovod;

  function hotovo(
    s: StavPolozky = rozpisane,
    n: string | undefined = nazov,
    m: (MaterialHrubky & { id: string }) | null = vybrany,
  ): void {
    // Materiál inej triedy hrúbky (0,8 k 1 mm) sa pýta — dielce dostanú
    // jeho hrúbku. Nezmenené priradenie (m je z iného id) sa nepýta znova.
    const zRiadku = hrubkaRiadku(polozka);
    const zMaterialu = m && m.id === s.material_id ? hrubkaViaze(m) : null;
    if (!s.zmazat && zRiadku !== null && zMaterialu !== null && inaTrieda(zRiadku, zMaterialu)) {
      setInaHrubka({ s, n, zRiadku, zMaterialu });
      return;
    }
    onZmena(s, n);
    onClose();
  }

  return (
    <VyberDialog
      open
      onOpenChange={(o) => !o && onClose()}
      nadpis={polozka.popis}
      popis={
        <>
          {kontext ? (
            <span className="block font-medium text-foreground" data-testid="vyber-kontext">
              {kontext}
            </span>
          ) : null}
          {autoPopis && !rozpisane.material_id
            ? autoPopis
            : `Vyber položku z cenníka pre tento názov z TopSolidu, alebo ju z importu vynechaj.${
                zhody.length > 0
                  ? " Bodky = ako dobre položka sedí s exportom, dôvod je v bubline."
                  : ""
              }`}
        </>
      }
      vybraneId={rozpisane.material_id}
      vybraneDoplnok={
        rozpisane.zmazat
          ? "Položka sa z importu vynechá."
          : dovod
            ? `zhoda: ${dovod}`
            : undefined
      }
      potvrdText="Hotovo"
      // Hotovo ide aj bez vybranej položky — Nenahrávať, alebo len zavrieť
      potvrdBezVyberu
      onPotvrd={() => hotovo()}
      akcie={
        <>
          {/* Zapamätať je od KOLA 204 len v riadku tabuľky, s nápisom
              (`Zapamatanie.tsx`) — dve fajky na to isté sa dali ťuknúť
              omylom a v okne nebolo vidno, čo sa zapamätá */}
          <span className="flex-1" />
          <Button
            type="button"
            size="sm"
            variant={rozpisane.zmazat ? "secondary" : "outline"}
            className="h-8 rounded-full"
            aria-pressed={rozpisane.zmazat}
            onClick={() =>
              setRozpisane((r) => ({ ...r, zmazat: !r.zmazat, material_id: null }))
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
            {rozpisane.zmazat ? "Nenahráva sa" : "Nenahrávať"}
          </Button>
          {/* Nový dekor v cenníku ešte nie je — dovtedy sa s takou
              položkou dalo spraviť jediné: „nenahrávať". */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 rounded-full"
            onClick={() => setZakladam(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Vytvoriť v cenníku
          </Button>
          {onKovanie ? (
            /* Export rozhoduje podľa `ZPRACOVANI=hw` a rozmerov, takže
               kód kovania (`F001249`) skončí medzi plošnými materiálmi.
               V tabuľke je to ikona, tu celý text — aby sa dala voľba
               nájsť aj bez uhádnutia ikony. */
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 rounded-full"
              onClick={() => {
                onKovanie();
                onClose();
              }}
            >
              <Wrench className="h-3.5 w-3.5" />
              Je to kovanie
            </Button>
          ) : null}
        </>
      }
    >
      <MaterialSelect
        vOkne
        value={rozpisane.material_id}
        onChange={(id, material) => {
          setRozpisane((r) => ({ ...r, material_id: id, zmazat: false }));
          setNazov(material ? (materialPrimaryId(material) ?? material.nazov) : undefined);
          setVybrany(material ?? null);
        }}
        // Dvojklik alebo Enter = priradiť a zavrieť
        onPotvrd={(m) =>
          hotovo(
            { ...rozpisane, material_id: m.id, zmazat: false },
            materialPrimaryId(m) ?? m.nazov,
            m,
          )
        }
        druh={filter.druh}
        sekcia={filter.sekcia}
        {...rozmerovyFilter(filter)}
        kategoriaIncludeCsv={filter.kategoriaIncludeCsv}
        zhody={zhody}
        autoFocus
        placeholder={`Hľadať v cenníku — ${polozka.popis}`}
      />

      {/* Ten istý formulár ako v Cenníku — druhý by sa časom rozišiel */}
      <MaterialEditDialog
        material={null}
        predvyplnene={predvyplnene}
        open={zakladam}
        onOpenChange={setZakladam}
        onCreated={(m) => {
          // Založené sa rovno priradí — inak by ho user musel hľadať
          setZakladam(false);
          hotovo(
            { ...rozpisane, material_id: m.id, zmazat: false },
            materialPrimaryId(m) ?? m.nazov,
            m,
          );
        }}
      />

      {inaHrubka ? (
        <InaHrubkaDialog
          polozka={polozka}
          zRiadku={inaHrubka.zRiadku}
          zMaterialu={inaHrubka.zMaterialu}
          material={inaHrubka.n}
          menaZostav={menaZostav}
          onSpat={() => setInaHrubka(null)}
          onPotvrd={() => {
            onZmena(inaHrubka.s, inaHrubka.n);
            onClose();
          }}
        />
      ) : null}
    </VyberDialog>
  );
}
